import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { BookOpen, Calculator, FlaskConical, Users, Bot, Sparkles, Brain, Rocket } from "lucide-react";

// Generate mesh background nodes
const generateMeshNodes = (count, width, height) => {
  const nodes = [];
  for (let i = 0; i < count; i++) {
    nodes.push({
      id: `mesh-${i}`,
      x: Math.random() * width,
      y: Math.random() * height,
      size: Math.random() * 3 + 1.5,
      opacity: Math.random() * 0.3 + 0.1,
    });
  }
  return nodes;
};

// Generate connections between mesh nodes
const generateMeshConnections = (nodes, maxDistance) => {
  const connections = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].x - nodes[j].x;
      const dy = nodes[i].y - nodes[j].y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < maxDistance) {
        connections.push({
          from: nodes[i],
          to: nodes[j],
          opacity: Math.max(0.05, 0.25 - (distance / maxDistance) * 0.2),
        });
      }
    }
  }
  return connections;
};

const ALL_SUBJECT_NODES = [
  {
    id: "comunicacion-lenguaje",
    name: "Comunicación",
    fullName: "Comunicación y Lenguaje",
    description: "Lectura, escritura y literatura",
    icon: BookOpen,
    color: "#ef4444",
    position: { x: 12, y: 20 },
  },
  {
    id: "ciencia-tecnologia",
    name: "Ciencia",
    fullName: "Ciencia y Tecnología",
    description: "Biología, física y química",
    icon: FlaskConical,
    color: "#14b8a6",
    position: { x: 88, y: 20 },
  },
  {
    id: "matematica-datos",
    name: "Matemática",
    fullName: "Matemática y Datos",
    description: "Números, álgebra y estadística",
    icon: Calculator,
    color: "#8b5cf6",
    position: { x: 12, y: 80 },
  },
  {
    id: "ciudadania-valores",
    name: "Ciudadanía",
    fullName: "Ciudadanía y Valores",
    description: "Derechos y democracia",
    icon: Users,
    color: "#f59e0b",
    position: { x: 88, y: 80 },
  },
  {
    id: "reto-avanzo",
    name: "Reto Avanzo",
    fullName: "Reto Avanzo",
    description: "Entrenamiento cognitivo adaptativo",
    icon: Brain,
    color: "#0ea5e9",
    position: { x: 50, y: 92 },
  },
  {
    // M3.1 UI closure — product-gated node (independent of subjects_enabled).
    // Visibility controlled by enabledProducts.buddy_avanza (see filter below).
    id: "buddy-avanza",
    name: "Buddy Avanza",
    fullName: "Buddy Avanza",
    description: "Entrenamiento PAES personalizado",
    icon: Rocket,
    color: "#f97316",
    position: { x: 50, y: 8 },
    isProduct: true,
  },
];

const subjectNodes = ALL_SUBJECT_NODES;

export const NetworkGraph = ({
  onNodeClick,
  onNodeOpen,
  onNodeTutor,
  hoveredNode,
  setHoveredNode,
  testIdPrefix = "network-graph",
  height = 550,
  showLabels = true,
  showTooltipOnHover = true,
  enabledSubjects = null, // optional: object map { "comunicacion-lenguaje": true, ... }
  enabledProducts = null, // M3.1: optional: object map { "buddy_avanza": true }
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1000, height: 550 });
  const [activeNode, setActiveNode] = useState(null);

  // Filter visible subject nodes based on `enabledSubjects` map and
  // product nodes on `enabledProducts` map.
  // Rules:
  //   - Subject nodes (isProduct != true) → default show; hide if enabledSubjects[key]===false
  //   - Reto Avanzo (id=reto-avanzo)      → hidden unless enabledSubjects.reto_avanzo===true
  //   - Product nodes (isProduct===true)  → hidden unless enabledProducts[key]===true
  const subjectNodes = useMemo(() => {
    const filtered = ALL_SUBJECT_NODES.filter((n) => {
      if (n.isProduct) {
        // Product-gated (M3.1): show only when explicit true
        const key = n.id === "buddy-avanza" ? "buddy_avanza" : n.id;
        return !!(enabledProducts && enabledProducts[key] === true);
      }
      if (n.id === "reto-avanzo") {
        // Reto Avanzo: enabled only when enabledSubjects.reto_avanzo === true
        return !!(enabledSubjects && enabledSubjects.reto_avanzo === true);
      }
      // Regular subjects: default visible unless explicitly false
      if (!enabledSubjects) return true;
      return enabledSubjects[n.id] !== false;
    });

    // When 5 or 6 nodes are visible, use dedicated layouts so the extra
    // nodes (reto-avanzo at bottom, buddy-avanza at top) don't collide
    // with the stats cards below the graph.
    if (filtered.length === 5) {
      const pentagonPositions = [
        { x: 12, y: 22 },  // 0 → top-left
        { x: 88, y: 22 },  // 1 → top-right
        { x: 18, y: 65 },  // 2 → middle-left
        { x: 82, y: 65 },  // 3 → middle-right
        { x: 50, y: 88 },  // 4 → bottom-center (reto-avanzo)
      ];
      const others = filtered.filter((n) => n.id !== "reto-avanzo" && n.id !== "buddy-avanza");
      const reto = filtered.find((n) => n.id === "reto-avanzo");
      const buddy = filtered.find((n) => n.id === "buddy-avanza");
      // If reto exists → bottom slot; if buddy exists but no reto → top slot instead
      if (reto) {
        return [
          ...others.slice(0, 4).map((n, i) => ({ ...n, position: pentagonPositions[i] })),
          { ...reto, position: pentagonPositions[4] },
        ];
      }
      if (buddy) {
        return [
          ...others.slice(0, 4).map((n, i) => ({ ...n, position: pentagonPositions[i] })),
          { ...buddy, position: { x: 50, y: 8 } },
        ];
      }
    }
    if (filtered.length === 6) {
      // Both buddy-avanza (top) and reto-avanzo (bottom) present.
      const hexPositions = [
        { x: 15, y: 30 },
        { x: 85, y: 30 },
        { x: 15, y: 70 },
        { x: 85, y: 70 },
      ];
      const others = filtered.filter((n) => n.id !== "reto-avanzo" && n.id !== "buddy-avanza");
      const reto = filtered.find((n) => n.id === "reto-avanzo");
      const buddy = filtered.find((n) => n.id === "buddy-avanza");
      return [
        ...(buddy ? [{ ...buddy, position: { x: 50, y: 10 } }] : []),
        ...others.slice(0, 4).map((n, i) => ({ ...n, position: hexPositions[i] })),
        ...(reto ? [{ ...reto, position: { x: 50, y: 90 } }] : []),
      ];
    }
    return filtered;
  }, [enabledSubjects, enabledProducts]);

  const [subjectPositions, setSubjectPositions] = useState(() =>
    subjectNodes.map((node) => ({ x: node.position.x, y: node.position.y }))
  );

  // Re-sync positions when the visible nodes change (toggle on/off subjects)
  useEffect(() => {
    setSubjectPositions(subjectNodes.map((node) => ({ x: node.position.x, y: node.position.y })));
  }, [subjectNodes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [dragDistance, setDragDistance] = useState(0);
  const [draggingNode, setDraggingNode] = useState(null);

  const dragThreshold = 14;
  const handleNodeOpen = onNodeOpen || onNodeClick;
  const handleNodeTutor = onNodeTutor || null;

  // Generate mesh background
  const meshNodes = useMemo(
    () => generateMeshNodes(90, dimensions.width, dimensions.height),
    [dimensions.width, dimensions.height]
  );
  const meshConnections = useMemo(
    () => generateMeshConnections(meshNodes, 150),
    [meshNodes]
  );

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: Math.max(320, rect.height) });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const clampPercent = (value) => Math.min(96, Math.max(4, value));

  const getSubjectPosition = React.useCallback((index) => {
    const base = subjectPositions[index] || subjectNodes[index].position;
    return {
      x: (base.x / 100) * dimensions.width,
      y: (base.y / 100) * dimensions.height,
    };
  }, [subjectPositions, subjectNodes, dimensions.width, dimensions.height]);

  const updateSubjectPosition = (index, point) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = point.x - rect.left;
    const y = point.y - rect.top;
    const xPct = clampPercent((x / dimensions.width) * 100);
    const yPct = clampPercent((y / dimensions.height) * 100);

    setSubjectPositions((prev) => {
      const next = [...prev];
      next[index] = { x: xPct, y: yPct };
      return next;
    });
  };

  const centerPosition = useMemo(() => {
    // When Reto Avanzo is enabled (5 subject nodes), shift the central
    // QHUB.BUDDY logo upwards so it doesn't overlap with the bottom-center node.
    const yPct = subjectNodes.length === 5 ? 38 : 50;
    return {
      x: dimensions.width / 2,
      y: (dimensions.height * yPct) / 100,
    };
  }, [dimensions.width, dimensions.height, subjectNodes.length]);

  // Generate connections from subject nodes to nearby mesh nodes
  const subjectToMeshConnections = useMemo(() => {
    const connections = [];
    subjectNodes.forEach((subjectNode, index) => {
      const pos = getSubjectPosition(index);
      meshNodes.forEach((meshNode) => {
        const dx = pos.x - meshNode.x;
        const dy = pos.y - meshNode.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < 180 && distance > 60) {
          connections.push({
            fromX: pos.x,
            fromY: pos.y,
            toX: meshNode.x,
            toY: meshNode.y,
            color: subjectNode.color,
            opacity: Math.max(0.1, 0.4 - (distance / 180) * 0.3),
          });
        }
      });
    });
    return connections;
  }, [meshNodes, subjectNodes, getSubjectPosition]);

  return (
    <div
      ref={containerRef}
      className="network-container relative w-full"
      style={{ height: typeof height === "number"
                ? `${subjectNodes.length === 5 ? Math.max(height, 700) : height}px`
                : height }}
      data-testid={testIdPrefix}
    >
      {/* Full SVG for mesh background and connections */}
      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}>
        {/* Background mesh connections */}
        {meshConnections.map((conn, i) => (
          <line
            key={`mesh-conn-${i}`}
            x1={conn.from.x}
            y1={conn.from.y}
            x2={conn.to.x}
            y2={conn.to.y}
            stroke="#cbd5e1"
            strokeWidth="1"
            opacity={conn.opacity}
          />
        ))}

        {/* Background mesh nodes */}
        {meshNodes.map((node) => (
          <circle
            key={node.id}
            cx={node.x}
            cy={node.y}
            r={node.size}
            fill="#94a3b8"
            opacity={node.opacity}
          />
        ))}

        {/* Connections from subject nodes to mesh */}
        {subjectToMeshConnections.map((conn, i) => (
          <line
            key={`subject-mesh-${i}`}
            x1={conn.fromX}
            y1={conn.fromY}
            x2={conn.toX}
            y2={conn.toY}
            stroke={conn.color}
            strokeWidth="1.5"
            opacity={conn.opacity}
          />
        ))}

        {/* Main connections between subject nodes */}
        {subjectNodes.map((node, i) => {
          const pos = getSubjectPosition(i);
          return (
            <React.Fragment key={`center-conn-${i}`}>
              {/* Connection to center */}
              <line
                x1={pos.x}
                y1={pos.y}
                x2={centerPosition.x}
                y2={centerPosition.y}
                stroke={hoveredNode === i ? node.color : "#e2e8f0"}
                strokeWidth={hoveredNode === i ? 3 : 2}
                strokeDasharray={hoveredNode === i ? "none" : "6 3"}
                opacity={hoveredNode === i ? 0.8 : 0.5}
              />
              {/* Connections to other subject nodes */}
              {subjectNodes.slice(i + 1).map((otherNode, j) => {
                const otherIndex = i + j + 1;
                const otherPos = getSubjectPosition(otherIndex);
                const isHighlighted = hoveredNode === i || hoveredNode === otherIndex;
                return (
                  <line
                    key={`node-conn-${i}-${j}`}
                    x1={pos.x}
                    y1={pos.y}
                    x2={otherPos.x}
                    y2={otherPos.y}
                    stroke={isHighlighted ? "#419cca" : "#e2e8f0"}
                    strokeWidth={isHighlighted ? 2 : 1}
                    strokeDasharray="4 2"
                    opacity={isHighlighted ? 0.6 : 0.3}
                  />
                );
              })}
            </React.Fragment>
          );
        })}
      </svg>

      {/* Central Hub - QHUB.BUDDY */}
      <motion.div
        className="absolute z-20"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        style={{
          left: "50%",
          top: subjectNodes.length === 5 ? "38%" : "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <div className="relative">
          {/* Outer glow ring */}
          <div className="absolute inset-[-25px] rounded-full bg-gradient-to-r from-[#419cca]/20 to-[#8b5cf6]/20 blur-xl" />
          
          {/* Dashed ring */}
          <div 
            className="absolute inset-[-15px] rounded-full border-2 border-dashed border-[#419cca]/30"
            style={{ animation: "spin 25s linear infinite" }}
          />
          
          {/* Main hub */}
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shadow-xl shadow-[#419cca]/30">
            <Bot className="w-12 h-12 text-white" />
            <Sparkles className="absolute -top-1 -right-1 w-6 h-6 text-yellow-300" />
          </div>
          
          {/* Label */}
          <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 text-center whitespace-nowrap">
            <span className="logo-text text-xl font-bold">QHUB.BUDDY</span>
          </div>
        </div>
      </motion.div>

      {/* Subject Nodes */}
      {subjectNodes.map((node, index) => {
        const pos = getSubjectPosition(index);
        const Icon = node.icon;
        const isHovered = hoveredNode === index;
        const isActive = activeNode === index;
        const isDragging = draggingNode === index;
        const showTooltip = isActive || (showTooltipOnHover && isHovered);
        const isHighlighted = isHovered || isActive;

        return (
          <motion.div
            key={node.id}
            className="absolute z-10 cursor-grab active:cursor-grabbing"
            style={{
              left: pos.x,
              top: pos.y,
              transform: "translate(-50%, -50%)",
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
              y: isDragging ? 0 : [0, -6, 0],
            }}
            transition={{
              scale: { duration: 0.4 },
              opacity: { duration: 0.4 },
              y: { duration: 3.2 + index * 0.3, repeat: Infinity, ease: "easeInOut" },
            }}
            drag
            dragConstraints={containerRef}
            dragElastic={0.2}
            dragMomentum={false}
            onDragStart={() => {
              setDraggingNode(index);
              setActiveNode(index);
            }}
            onDrag={(event, info) => {
              updateSubjectPosition(index, info.point);
            }}
            onDragEnd={(event, info) => {
              setDraggingNode(null);
              const distance = Math.hypot(info.offset.x, info.offset.y);
              setDragDistance(distance);
              window.setTimeout(() => setDragDistance(0), 150);
              updateSubjectPosition(index, info.point);
              if (distance > dragThreshold && handleNodeOpen) {
                handleNodeOpen(node.id);
              }
            }}
            onMouseEnter={() => setHoveredNode(index)}
            onMouseLeave={() => setHoveredNode(null)}
            onClick={() => {
              if (dragDistance > dragThreshold) {
                setDragDistance(0);
                return;
              }
              const nextActive = activeNode === index ? null : index;
              setActiveNode(nextActive);
              setHoveredNode(nextActive === null ? null : index);
            }}
            whileHover={{ scale: 1.08 }}
            whileDrag={{ scale: 1.05 }}
            data-testid={`${testIdPrefix}-node-${node.id}`}
          >
            {/* Outer glow on hover */}
            {isHighlighted && (
              <div 
                className="absolute inset-[-20px] rounded-full blur-xl"
                style={{ backgroundColor: `${node.color}30` }}
              />
            )}

            {/* Connection lines radiating out (visual effect) */}
            <svg 
              className="absolute pointer-events-none"
              style={{ 
                left: "50%", 
                top: "50%", 
                transform: "translate(-50%, -50%)",
                width: 200,
                height: 200,
              }}
              viewBox="0 0 200 200"
            >
              {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
                const rad = (angle * Math.PI) / 180;
                const innerR = 50;
                const outerR = isHighlighted ? 95 : 85;
                return (
                  <line
                    key={i}
                    x1={100 + Math.cos(rad) * innerR}
                    y1={100 + Math.sin(rad) * innerR}
                    x2={100 + Math.cos(rad) * outerR}
                    y2={100 + Math.sin(rad) * outerR}
                    stroke={node.color}
                    strokeWidth={isHighlighted ? 2 : 1}
                    opacity={isHighlighted ? 0.6 : 0.3}
                  />
                );
              })}
            </svg>

            {/* Dashed orbit ring */}
            <div 
              className="absolute inset-[-12px] rounded-full border-2 border-dashed transition-all duration-300"
              style={{ 
                borderColor: isHighlighted ? node.color : `${node.color}40`,
                animation: "spin 20s linear infinite"
              }}
            />

            {/* Main node circle */}
            <div
              className="relative w-28 h-28 rounded-full bg-white flex flex-col items-center justify-center transition-all duration-300"
              style={{
                border: `4px solid ${node.color}`,
                boxShadow: isHighlighted 
                  ? `0 8px 32px ${node.color}40, 0 0 0 8px ${node.color}15`
                  : `0 4px 20px rgba(0,0,0,0.1)`,
              }}
            >
              {/* Icon */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-1"
                style={{ backgroundColor: `${node.color}15` }}
              >
                <Icon className="w-6 h-6" style={{ color: node.color }} />
              </div>
              {showLabels && (
                <span 
                  className="text-xs font-bold"
                  style={{ color: node.color }}
                >
                  {node.name}
                </span>
              )}
            </div>

            {/* Hover tooltip */}
            {showTooltip && (
              <motion.div
                className="absolute z-30 bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 w-64"
                style={{
                  top: index < 2 ? "100%" : "auto",
                  bottom: index >= 2 ? "100%" : "auto",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginTop: index < 2 ? "20px" : 0,
                  marginBottom: index >= 2 ? "20px" : 0,
                }}
                initial={{ opacity: 0, y: index < 2 ? -10 : 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${node.color}15` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: node.color }} />
                  </div>
                  <span className="font-bold text-gray-900">{node.fullName}</span>
                </div>
                <p className="text-sm text-gray-500 mb-4">{node.description}</p>
                {isActive ? (
                  <div className="space-y-2" data-testid={`${testIdPrefix}-node-options-${node.id}`}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 rounded-xl text-sm font-semibold border"
                      style={{ borderColor: node.color, color: node.color }}
                      onClick={() => handleNodeTutor && handleNodeTutor(node.id)}
                      data-testid={`${testIdPrefix}-node-tutor-${node.id}`}
                    >
                      Abrir tutor
                    </button>
                    {handleNodeOpen && (
                      <p className="text-xs text-gray-400">Arrastra el nodo para abrir la materia.</p>
                    )}
                  </div>
                ) : (
                  <div
                    className="text-sm font-semibold flex items-center gap-2"
                    style={{ color: node.color }}
                  >
                    <span>Click para ver el nombre</span>
                    <span>→</span>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        );
      })}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default NetworkGraph;
